import * as React from 'react';
import classNames from 'classnames';
import Label from '../Label';
import TextInput, { TextInputProps } from '../TextInput';
import PhoneInput, { PhoneInputProps } from '../PhoneInput';
import Textarea from '../Textarea';

const TextField = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>((props, ref) => {
  const {
    label,
    name,
    placeholderText,
    labelClassName,
    inputClassName,
    formGroupClassName,
    inputWrapperClassName,
    inRow,
    type,
    value,
    ...otherProps
  } = props;
  const textFieldClasses = classNames('ui-form-group', formGroupClassName, {
    'ui-form-group--row': inRow,
  });
  const phoneValue: string | undefined =
    typeof value === 'number' ? value.toString() : value;
  let InnerComponent;

  switch (type) {
    case 'phone': {
      InnerComponent = (
        <PhoneInput
          name={name}
          className={inputClassName}
          wrapperClassName={inputWrapperClassName}
          value={phoneValue}
          {...otherProps}
          ref={ref as React.ForwardedRef<HTMLInputElement>}
        />
      );

      break;
    }
    case 'textarea': {
      InnerComponent = (
        <Textarea
          name={name}
          className={inputClassName}
          wrapperClassName={inputWrapperClassName}
          value={value as string}
          {...otherProps}
          ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
        />
      );

      break;
    }
    default: {
      InnerComponent = (
        <TextInput
          name={name}
          className={inputClassName}
          wrapperClassName={inputWrapperClassName}
          type={type}
          value={value}
          {...otherProps}
          ref={ref as React.ForwardedRef<HTMLInputElement>}
        />
      );
    }
  }

  return (
    <div className={textFieldClasses}>
      {label && (
        <Label
          id={name}
          label={label}
          placeholderText={placeholderText}
          className={labelClassName}
        />
      )}
      {InnerComponent}
    </div>
  );
});

export interface TextFieldProps
  extends Omit<TextInputProps, 'value' | 'onChange' | 'onBlur'>,
    Omit<PhoneInputProps, 'value' | 'onChange'> {
  name: string;
  type?: string;
  label?: string | React.ReactNode;
  placeholderText?: string;
  labelClassName?: string;
  inputClassName?: string;
  formGroupClassName?: string;
  inputWrapperClassName?: string;
  inRow?: boolean;
  value?: string | number;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export default TextField;
